import { Stack, Box, Typography } from '@mui/material';
import { POSITION_FILL_REQUESTS_PATH } from 'domain/shared/positionFillRequest';
import { EmployeeLinkWithNameLoading } from 'domain/shared/employee';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import type { PreviewPositionSlotListItem } from '../../types';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Tooltip } from 'framework/ui/mui-builder';
import PositionSlotValidationErrorsTooltip from 'domain/people/positionSlots/field/PositionSlotValidationErrorsTooltip';
import { usePositionSlotValidationErrors } from '../../List/PositionSlotValidationContext';
import { SimpleLinkField } from 'framework/ViewTypes';
import { Features } from 'infrastructure/featureToggle';
import useFeatureToggle from 'framework/featureToggle';

const buildWorkerWarnings = (positionSlot: PreviewPositionSlotListItem, validationErrors: string[]) => {
  const employeeAliases = positionSlot.employeeAliases ?? [];
  return employeeAliases.length > 1
    ? [...validationErrors, `Multiple employees assigned: ${employeeAliases.join(', ')}`]
    : validationErrors;
};

const WorkerWarningIcon = ({ errors }: { errors: string[] }) => (
  <Tooltip title={<PositionSlotValidationErrorsTooltip errors={errors} />}>
    <PriorityHighIcon color="error" fontSize="small" />
  </Tooltip>
);

const WorkerAndPfrCell = ({ positionSlot }: { positionSlot: PreviewPositionSlotListItem }) => {
  const { isFeatureEnabled } = useFeatureToggle();
  const validationErrors = usePositionSlotValidationErrors(positionSlot.employeeAlias);
  const warnings = buildWorkerWarnings(positionSlot, validationErrors);

  const employeeLink = positionSlot.employeeAlias && (
    <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
      <Box minWidth={0}>
        <EmployeeLinkWithNameLoading id={positionSlot.employeeAlias} />
      </Box>
      {positionSlot.requiredReplacement && (
        <Tooltip title="Replacement required">
          <SyncAltIcon color="error" fontSize="small" />
        </Tooltip>
      )}
      {warnings.length > 0 && <WorkerWarningIcon errors={warnings} />}
    </Stack>
  );

  const pfrLink = positionSlot.positionFulfillmentRequestId &&
    isFeatureEnabled(Features.POSITION_FILL_REQUESTS_COLLECTION) && (
      <SimpleLinkField
        to={`${POSITION_FILL_REQUESTS_PATH}/${positionSlot.positionFulfillmentRequestId}`}
        value={positionSlot.positionFulfillmentRequestId}
      />
    );

  if (employeeLink && pfrLink) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
        {employeeLink}
        <Typography color="textSecondary">/</Typography>
        {pfrLink}
      </Stack>
    );
  }

  return employeeLink || pfrLink || EMPTY_VALUE_INDICATOR;
};

export default WorkerAndPfrCell;
